import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, FileText } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { BlogPost } from '../data/blogPosts';
import { fetchPosts } from '../services/blogService';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const categories = ['All', 'Small Business', 'GenAI', 'Architecture', 'Engineering'];

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchPosts(activeFilter).then((result) => {
      setPosts(result.posts);
      setLoading(false);
    });
  }, [activeFilter]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 200);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleFilterChange = (cat: string) => {
    setSearchQuery('');
    setActiveFilter(cat);
  };

  const filtered = posts.filter((p) => {
    const matchesSearch = searchQuery === '' || p.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div>
      {/* ─── Hero ─── */}
      <section
        className="hero-fade-in hero-padding-center"
        style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 50%, #0F172A 100%)',
          borderRadius: '24px',
          marginBottom: '32px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(245,158,11,0.06)', filter: 'blur(60px)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 16px',
              background: 'rgba(245,158,11,0.15)',
              color: '#F59E0B',
              borderRadius: '9999px',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '20px',
              border: '1px solid rgba(245,158,11,0.2)',
            }}
          >
            <FileText size={14} /> Insights & Articles
          </span>
          <h1
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              fontFamily: 'var(--font-heading)',
              color: '#fff',
              marginBottom: '16px',
            }}
          >
            Blog
          </h1>
          <p
            style={{
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.7,
              maxWidth: '560px',
              marginLeft: 'auto',
              marginRight: 'auto',
              marginBottom: '32px',
            }}
          >
            Technical articles on scalable architecture, Generative AI, and production engineering.
          </p>

          <div style={{ maxWidth: '400px', margin: '0 auto', position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-gray-400)' }} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px 14px 44px',
                borderRadius: '12px',
                border: '2px solid rgba(255,255,255,0.15)',
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#F59E0B'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
            />
          </div>
        </div>
      </section>

      {/* ─── Filter Bar ─── */}
      <div
        style={{
          position: 'sticky',
          top: isScrolled ? '58px' : '80px',
          zIndex: 10,
          background: 'rgba(250,250,250,0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          padding: '16px 0',
          borderBottom: '1px solid var(--color-gray-200)',
          marginBottom: '40px',
          transition: 'top 0.3s',
        }}
      >
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleFilterChange(cat)}
              style={{
                padding: '8px 20px',
                borderRadius: '9999px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.88rem',
                fontWeight: 600,
                background: activeFilter === cat ? 'var(--color-primary)' : 'var(--color-gray-100)',
                color: activeFilter === cat ? '#fff' : 'var(--color-gray-600)',
                transition: 'all 0.2s',
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Post Grid ─── */}
      <div
        className="grid-cards"
        style={{
          marginBottom: '64px',
        }}
      >
        {filtered.map((post, i) => (
          <AnimatedSection key={post.id} delay={i * 100}>
            <Link to={`/blog/${post.slug || post.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
              <div
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                  border: '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)';
                }}
              >
                <div style={{ height: '160px', background: post.gradient }} />
                <div style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span
                      style={{
                        padding: '3px 10px',
                        background: 'rgba(30,58,95,0.08)',
                        color: 'var(--color-primary)',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      {post.category}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-gray-400)' }}>
                      <Clock size={14} /> {post.readTime} min
                    </span>
                  </div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '10px', lineHeight: 1.4 }}>{post.title}</h3>
                  <p style={{ color: 'var(--color-gray-500)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '12px' }}>
                    {post.excerpt}
                  </p>
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-gray-400)' }}>{post.date}</p>
                </div>
              </div>
            </Link>
          </AnimatedSection>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--color-gray-400)' }}>
          <p style={{ fontSize: '1.1rem' }}>Loading articles...</p>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--color-gray-400)' }}>
          <p style={{ fontSize: '1.1rem' }}>No articles match your search.</p>
        </div>
      )}

    </div>
  );
};

export default Blog;
